import type { MutableRefObject } from 'react';
import { useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import { useFormContext, useWatch } from 'react-hook-form';

import { useDynamicCoverageRules } from '@/shared/hooks';
import { calculateLeaseDurationMonths } from '@/shared/utils/leaseDurationMonths.util';
import { RentCoverageTypes, SecurityDepositTypes } from '@/constants/CoverageType';
import type { CoveragePricing } from '@/shared/types';
import { SELF_ENROLLED_BUILDING_ATTR } from '@/utils/constants';
import { useInviteLeaseDetailsDrawerStore } from '@/stores/use-invite-lease-details-drawer.store';

import { ScreeningResult } from './constants';
import {
  useSmbCoverageOptions,
  useTrackCoveragePricingResolved,
  useTrackScreeningLabelsLoaded,
} from './hooks';
import { staticInstitutionalScreeningResultsOptions } from './screening-results.options';
import { hasCustomCoverageRuleLabels, resolveDynamicCoverageOptions } from './utils';

// The imperative glue the declarative config can't express, hosted as a (null-
// rendering) slot inside the engine's FormProvider. Ports every effect the
// legacy `InviteLeaseDetailsSharedBody` / screening-results component ran:
// customCoverage cascade, address→self-enrolled lift, DDS pricing + auto-select,
// pricing/labels tracking, the linked lease-date auto-fill, and dirty sync.

const DEFAULT_LEASE_DURATION_MONTHS = 12;

const toDate = (iso?: string | null): Date | undefined => (iso ? new Date(iso) : undefined);
const toNumber = (value?: string | null): number | undefined => {
  if (value == null || value === '') {
    return undefined;
  }
  const parsed = Number(String(value).replaceAll(',', ''));
  return Number.isFinite(parsed) ? parsed : undefined;
};

export type InviteLeaseDetailsEffectsProps = {
  isSmbInvite: boolean;
  newCoveragesEnabled: boolean;
  onSelfEnrolledChange: (isSelfEnrolled: boolean) => void;
  selectedSmbPricingRef: MutableRefObject<CoveragePricing | undefined>;
};

export function InviteLeaseDetailsEffects({
  isSmbInvite,
  newCoveragesEnabled,
  onSelfEnrolledChange,
  selectedSmbPricingRef,
}: InviteLeaseDetailsEffectsProps) {
  const { control, watch, setValue, resetField, formState } = useFormContext();
  const { isDirty, dirtyFields } = formState;

  // Mirror dirty state into the drawer store (drives close-confirmation).
  const dirtyStateRef = useInviteLeaseDetailsDrawerStore((state) => state.dirtyStateRef);
  dirtyStateRef.current = { isDirty, dirtyFields };

  const address = useWatch({ control, name: 'address' });
  const customCoverage = useWatch({ control, name: 'customCoverage' });
  const leaseStartRaw = useWatch({ control, name: 'leaseStartDate' });
  const leaseEndRaw = useWatch({ control, name: 'leaseEndDate' });
  const grossMonthlyRentRaw = useWatch({ control, name: 'grossMonthlyRent' });
  const prepaidRentRaw = useWatch({ control, name: 'prepaidRent' });
  const freeRentRaw = useWatch({ control, name: 'freeRent' });
  const monthsOfCoverageRaw = useWatch({ control, name: 'leaseGuaranteeMonthsOverride' });
  const selectedCoverageRuleId = useWatch({ control, name: 'selectedCoverageRuleId' });
  const screeningStatus = useWatch({ control, name: 'screeningStatus' });
  // Institutional non-PMI option-box toggles. Drive override-field visibility
  // in form state, but their values are transient UI — the wire payload's
  // coverage type is derived from the override fields' presence at submit.
  const rentCoverageToggle = useWatch({ control, name: 'rentCoverage' });
  const securityDepositCoverageToggle = useWatch({ control, name: 'securityDepositCoverage' });

  const leaseStartDate = toDate(leaseStartRaw);
  const leaseEndDate = toDate(leaseEndRaw);
  const grossMonthlyRent = toNumber(grossMonthlyRentRaw);
  const prepaidRent = toNumber(prepaidRentRaw);
  const freeRent = toNumber(freeRentRaw);
  const leaseGuaranteeMonthsOverride = toNumber(monthsOfCoverageRaw);

  // PMI customCoverage cascade. Skips when undefined (non-PMI variants).
  // Coverage type is now derived from override-field presence at submit, so we
  // only need to (a) clear screeningStatus when entering customize mode, and
  // (b) clear the override fields when exiting it — otherwise stale values
  // would survive the hide and the bridge would derive `Customize`.
  useEffect(() => {
    if (customCoverage === undefined) {
      return;
    }
    if (customCoverage) {
      resetField('screeningStatus');
    } else {
      setValue('leaseGuaranteeMonthsOverride', null, { shouldValidate: false });
      setValue('securityDepositReplacementAmountOverride', null, { shouldValidate: false });
    }
  }, [customCoverage, resetField, setValue]);

  // Institutional non-PMI: when an option-box flips to "Company default", clear
  // the matching override so the bridge derives `CompanyDefault`. The engine
  // hides the override field via visibility rule, but it doesn't unregister, so
  // without this a previously-typed value would survive the hide.
  useEffect(() => {
    if (isSmbInvite) {
      return;
    }
    if (rentCoverageToggle === RentCoverageTypes.CompanyDefault) {
      setValue('leaseGuaranteeMonthsOverride', null, { shouldValidate: false });
    }
  }, [rentCoverageToggle, isSmbInvite, setValue]);

  useEffect(() => {
    if (isSmbInvite) {
      return;
    }
    if (securityDepositCoverageToggle === SecurityDepositTypes.CompanyDefault) {
      setValue('securityDepositReplacementAmountOverride', null, { shouldValidate: false });
    }
  }, [securityDepositCoverageToggle, isSmbInvite, setValue]);

  // Address-derived self-enrolled state lifts to the parent to size the SDR cap.
  useEffect(() => {
    onSelfEnrolledChange(address?.source === SELF_ENROLLED_BUILDING_ATTR);
  }, [address, onSelfEnrolledChange]);

  // Always derive end from start: `start + 12 months - 1 day`. Re-runs on every
  // start-date change, including after the user edited the end date — invite's
  // default term is one year from the chosen start.
  useEffect(() => {
    if (!leaseStartRaw) {
      return;
    }
    const derived = dayjs(leaseStartRaw)
      .add(DEFAULT_LEASE_DURATION_MONTHS, 'month')
      .subtract(1, 'day');
    if (!derived.isValid()) {
      return;
    }
    setValue('leaseEndDate', derived.toISOString(), { shouldValidate: true, shouldDirty: false });
  }, [leaseStartRaw, setValue]);

  const leaseDurationMonths = calculateLeaseDurationMonths(leaseStartDate, leaseEndDate);

  // ---- SMB (new coverages) dynamic pricing ----
  const {
    coverageOptions,
    selectedCoverageOption,
    isUsingDynamicPricing,
    isLoading: smbLoading,
    isError: smbError,
    error: smbErr,
  } = useSmbCoverageOptions({
    propertyId: address?.property_id,
    leaseStart: leaseStartDate,
    leaseEnd: leaseEndDate,
    grossMonthlyRent,
    prepaidRent,
    freeRent,
    leaseDurationMonths,
    selectedValue: leaseGuaranteeMonthsOverride,
    selectedId: selectedCoverageRuleId,
    enabled: newCoveragesEnabled && isSmbInvite,
  });

  useEffect(() => {
    selectedSmbPricingRef.current = selectedCoverageOption?.pricing;
  }, [selectedCoverageOption, selectedSmbPricingRef]);

  useTrackCoveragePricingResolved({
    propertyId: address?.property_id,
    enabled: isSmbInvite && isUsingDynamicPricing,
    isLoading: smbLoading,
    isError: smbError,
    error: smbErr,
    hasCustomScreeningStatusLabels: false,
    pricingOptionCount: coverageOptions?.length ?? 0,
    pricingData: coverageOptions,
  });

  // SMB screening auto-select from the chosen coverage option.
  useEffect(() => {
    if (!newCoveragesEnabled || !isSmbInvite) {
      return;
    }

    if (!leaseGuaranteeMonthsOverride) {
      setValue('screeningStatus', '', { shouldValidate: false });
      setValue('selectedCoverageRuleId', null);
      return;
    }
    if (!selectedCoverageOption || selectedCoverageOption.disabled) {
      setValue('screeningStatus', '', { shouldValidate: false });
      setValue('leaseGuaranteeMonthsOverride', null);
      setValue('selectedCoverageRuleId', null);
      return;
    }
    if (customCoverage) {
      setValue('screeningStatus', ScreeningResult.Denied, { shouldValidate: false });
      return;
    }
    if (watch('screeningStatus') !== selectedCoverageOption.screeningResult) {
      setValue('screeningStatus', selectedCoverageOption.screeningResult, {
        shouldValidate: false,
      });
    }
  }, [
    customCoverage,
    isSmbInvite,
    leaseGuaranteeMonthsOverride,
    newCoveragesEnabled,
    selectedCoverageOption,
    setValue,
    watch,
  ]);

  // ---- Institutional dynamic rules: tracking + auto-clear ----
  const isDynamicEnabled = Boolean(address?.property_id && !isSmbInvite);
  const {
    data: institutionalRules,
    isLoading: institutionalLoading,
    isError: institutionalError,
    error: institutionalErr,
  } = useDynamicCoverageRules({
    propertyId: address?.property_id,
    leaseStart: leaseStartDate,
    leaseEnd: leaseEndDate,
    grossMonthlyRent,
    prepaidRent,
    freeRent,
    enabled: isDynamicEnabled,
  });

  const institutionalHasCustomLabels =
    !isSmbInvite && hasCustomCoverageRuleLabels(institutionalRules);
  const institutionalHasPricing = (institutionalRules ?? []).some((rule) => Boolean(rule.pricing));
  const addressSelected = Boolean(address?.monolith_building_id);

  const institutionalOptions = useMemo(
    () =>
      isSmbInvite || !addressSelected
        ? []
        : resolveDynamicCoverageOptions(
            staticInstitutionalScreeningResultsOptions,
            institutionalRules,
          ),
    [isSmbInvite, addressSelected, institutionalRules],
  );

  useEffect(() => {
    if (isSmbInvite) {
      return;
    }
    const selected = institutionalOptions.find((option) => option.value === screeningStatus);
    if (!institutionalLoading && screeningStatus && (!selected || selected.disabled)) {
      setValue('screeningStatus', '', { shouldValidate: true });
    }
  }, [isSmbInvite, institutionalLoading, screeningStatus, institutionalOptions, setValue]);

  useTrackScreeningLabelsLoaded({
    propertyId: address?.property_id ?? address?.monolith_building_id,
    isLoading: institutionalLoading,
    isError: institutionalError,
    error: institutionalErr,
    hasCustomScreeningStatusLabels: institutionalHasCustomLabels,
    labelCount: institutionalOptions.length,
    enabled: !isSmbInvite && !institutionalHasPricing,
  });

  useTrackCoveragePricingResolved({
    propertyId: address?.property_id,
    enabled: !isSmbInvite && institutionalHasPricing,
    isLoading: institutionalLoading,
    isError: institutionalError,
    error: institutionalErr,
    hasCustomScreeningStatusLabels: institutionalHasCustomLabels,
    pricingOptionCount: institutionalRules?.length ?? 0,
    pricingData: institutionalRules,
  });

  return null;
}
